import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';
import { Save, Building2, Zap, Flame, Loader2, RefreshCw, Calculator, Link as LinkIcon, Info, ChevronDown, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PORTALI = ['Supermoney', 'Promoup', 'ComparaSemplice', 'Egea', 'Octopus Ref', 'Diretto Fornitore', 'Altro'];

const Commissioni = () => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole(['super_admin']);
  const [commissioni, setCommissioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [initializing, setInitializing] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [recalculating, setRecalculating] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const fetchCommissioni = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/commissioni`, { params: showAll ? { show_all: true } : {} });
      setCommissioni(res.data);
      const vals = {};
      res.data.forEach(c => {
        vals[c.id] = {
          base_luce: c.base_luce ?? c.super_admin_luce ?? 0,
          base_gas: c.base_gas ?? c.super_admin_gas ?? 0,
          base_dual: c.base_dual ?? c.super_admin_dual ?? 0,
          bonus_rid: c.bonus_rid ?? 0,
          bonus_bolletta_web: c.bonus_bolletta_web ?? 0,
          cascata_agenzia_pct: c.cascata_agenzia_pct ?? 80,
          cascata_sub_agenzia_pct: c.cascata_sub_agenzia_pct ?? 65,
          portali_caricamento: c.portali_caricamento || [],
          calcolo_al_pun: !!c.calcolo_al_pun,
          link_referral: c.link_referral || '',
          note: c.note || '',
        };
      });
      setEditValues(vals);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel caricamento commissioni');
    } finally {
      setLoading(false);
    }
  }, [showAll]);

  useEffect(() => { fetchCommissioni(); }, [fetchCommissioni]);

  const handleInit = async () => {
    setInitializing(true);
    try {
      const res = await axios.post(`${API_URL}/api/commissioni/init`);
      toast.success(res.data.message);
      fetchCommissioni();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore inizializzazione');
    } finally {
      setInitializing(false);
    }
  };

  const handleSave = async (id) => {
    setSaving(id);
    try {
      const vals = editValues[id];
      await axios.put(`${API_URL}/api/commissioni/${id}`, vals);
      const comp = commissioni.find(c => c.id === id)?.compagnia || '';
      toast.success(`Provvigioni ${comp} aggiornate`);
      fetchCommissioni();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(null);
    }
  };

  const handleRecalculate = async () => {
    if (!window.confirm('Vuoi ricalcolare le commissioni di tutte le pratiche aperte (non ancora pagate)? Questa operazione applica le nuove regole alle pratiche esistenti.')) return;
    setRecalculating(true);
    try {
      const res = await axios.post(`${API_URL}/api/commissioni/ricalcola-pratiche`, { dry_run: false });
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore ricalcolo');
    } finally {
      setRecalculating(false);
    }
  };

  const updateField = (id, field, value, isNumeric = true) => {
    setEditValues(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: isNumeric ? (parseFloat(value) || 0) : value }
    }));
  };

  const togglePortale = (id, portale) => {
    setEditValues(prev => {
      const current = prev[id]?.portali_caricamento || [];
      const next = current.includes(portale) ? current.filter(p => p !== portale) : [...current, portale];
      return { ...prev, [id]: { ...prev[id], portali_caricamento: next } };
    });
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  // Compute live preview for a given commission plan
  const computePreview = (vals, tipologia, metodo = 'rid', tipoBolletta = 'web') => {
    if (!vals) return null;
    const base = parseFloat(vals[`base_${tipologia}`] || 0);
    const rid = metodo === 'rid' ? parseFloat(vals.bonus_rid || 0) : 0;
    const web = tipoBolletta === 'web' ? parseFloat(vals.bonus_bolletta_web || 0) : 0;
    const master = base + rid + web;
    const pctA = parseFloat(vals.cascata_agenzia_pct || 80);
    const pctS = parseFloat(vals.cascata_sub_agenzia_pct || 65);
    return {
      master,
      agenzia: Math.round(master * pctA) / 100 * 100 / 100,
      sub: Math.round(master * pctS) / 100 * 100 / 100,
      _agenzia: +(master * pctA / 100).toFixed(2),
      _sub: +(master * pctS / 100).toFixed(2),
    };
  };

  // --- SUB-AGENT VIEW (sees only their cascade percentages) ---
  if (!isAdmin) {
    return (
      <div className="space-y-6" data-testid="commissioni-page">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Le Mie Provvigioni</h1>
          <p className="text-slate-500 mt-1">Importi che ricevi per ogni contratto attivato</p>
        </div>
        {commissioni.length === 0 ? (
          <Card><CardContent className="py-16 text-center text-slate-500">Nessuna provvigione disponibile</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {commissioni.map(c => (
              <Card key={c.id} data-testid={`mie-prov-${c.compagnia}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <h3 className="font-semibold text-slate-900">{c.compagnia}</h3>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    {c.tua_provvigione_luce > 0 && (
                      <div className="flex justify-between"><span className="flex items-center gap-1"><Zap className="w-3 h-3 text-amber-500" />Luce</span><strong className="tabular-nums">{c.tua_provvigione_luce.toFixed(2)}€</strong></div>
                    )}
                    {c.tua_provvigione_gas > 0 && (
                      <div className="flex justify-between"><span className="flex items-center gap-1"><Flame className="w-3 h-3 text-sky-500" />Gas</span><strong className="tabular-nums">{c.tua_provvigione_gas.toFixed(2)}€</strong></div>
                    )}
                    {c.tua_provvigione_dual > 0 && (
                      <div className="flex justify-between"><span>Dual</span><strong className="tabular-nums">{c.tua_provvigione_dual.toFixed(2)}€</strong></div>
                    )}
                    {(c.bonus_rid > 0 || c.bonus_bolletta_web > 0) && (
                      <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-500">
                        {c.bonus_rid > 0 && <div>+ RID Bancario: <strong className="text-emerald-600">+{c.bonus_rid.toFixed(2)}€</strong></div>}
                        {c.bonus_bolletta_web > 0 && <div>+ Bolletta Web: <strong className="text-emerald-600">+{c.bonus_bolletta_web.toFixed(2)}€</strong></div>}
                      </div>
                    )}
                  </div>
                  {c.portali_caricamento && c.portali_caricamento.length > 0 && (
                    <div className="mt-3 pt-2 border-t border-slate-100">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Carica su:</p>
                      <div className="flex flex-wrap gap-1">
                        {c.portali_caricamento.map(p => <Badge key={p} variant="outline" className="text-[10px]">{p}</Badge>)}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // --- MASTER (super_admin) VIEW ---
  if (loading) return <div className="space-y-4"><Skeleton className="h-10 w-72" /><Skeleton className="h-96 w-full" /></div>;

  return (
    <div className="space-y-6" data-testid="commissioni-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Listino Provvigioni Master</h1>
          <p className="text-slate-500 mt-1">Imposta base + bonus per ogni compagnia. Le agenzie e i sub-agenti ricevono in cascata.</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer pr-2">
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="rounded" data-testid="toggle-show-all-comm" />
            Mostra anche disattivate
          </label>
          <Button variant="outline" size="sm" onClick={handleRecalculate} disabled={recalculating} data-testid="recalc-pratiche-btn">
            <Calculator className={`w-4 h-4 mr-1.5 ${recalculating ? 'animate-pulse' : ''}`} />
            {recalculating ? 'Ricalcolo...' : 'Ricalcola Pratiche Aperte'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleInit} disabled={initializing} data-testid="init-commissioni-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${initializing ? 'animate-spin' : ''}`} />
            Sincronizza Compagnie
          </Button>
        </div>
      </div>

      {commissioni.length === 0 ? (
        <Card><CardContent className="py-16 text-center">
          <p className="text-slate-500 text-lg mb-4">Nessun piano commissioni configurato</p>
          <Button onClick={handleInit}>Inizializza dalle Compagnie</Button>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {commissioni.map((c) => {
            const v = editValues[c.id] || {};
            const isOpen = expanded[c.id];
            const previewLuce = computePreview(v, 'luce', 'rid', 'web');
            return (
              <Card key={c.id} data-testid={`commissione-${c.id}`}>
                <CardContent className="p-0">
                  {/* Header row */}
                  <div className="flex items-center gap-4 p-4 cursor-pointer hover:bg-slate-50" onClick={() => toggleExpand(c.id)}>
                    {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <Building2 className="w-5 h-5 text-slate-400" />
                    <h3 className="text-base font-bold text-slate-900 flex-1">{c.compagnia}</h3>
                    {/* Quick preview pills */}
                    {previewLuce && previewLuce.master > 0 && (
                      <div className="hidden md:flex items-center gap-2 text-xs">
                        <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">Master: {previewLuce.master.toFixed(2)}€</Badge>
                        <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100">Agenzia: {previewLuce._agenzia.toFixed(2)}€</Badge>
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Sub: {previewLuce._sub.toFixed(2)}€</Badge>
                      </div>
                    )}
                    {(v.portali_caricamento || []).length > 0 && (
                      <div className="hidden lg:flex flex-wrap gap-1">
                        {(v.portali_caricamento || []).slice(0, 2).map(p => <Badge key={p} variant="outline" className="text-[10px]">{p}</Badge>)}
                        {(v.portali_caricamento || []).length > 2 && <Badge variant="outline" className="text-[10px]">+{(v.portali_caricamento || []).length - 2}</Badge>}
                      </div>
                    )}
                  </div>

                  {/* Expanded panel */}
                  {isOpen && (
                    <div className="border-t border-slate-100 p-5 space-y-5 bg-slate-50/30">
                      {/* Base provvigioni */}
                      <div>
                        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">1. Base Provvigione per Tipologia</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {['luce', 'gas', 'dual'].map(tipo => (
                            <div key={tipo} className="space-y-1">
                              <label className="text-xs text-slate-500 capitalize flex items-center gap-1">
                                {tipo === 'luce' && <Zap className="w-3 h-3 text-amber-500" />}
                                {tipo === 'gas' && <Flame className="w-3 h-3 text-sky-500" />}
                                {tipo}
                              </label>
                              <div className="relative">
                                <Input type="number" step="0.01" min="0" value={v[`base_${tipo}`] ?? 0} onChange={(e) => updateField(c.id, `base_${tipo}`, e.target.value)} className="h-9 pr-8" data-testid={`base-${c.id}-${tipo}`} />
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">€</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Bonus */}
                      <div>
                        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">2. Bonus Additivi</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-xs text-slate-500">Bonus RID Bancario</label>
                            <div className="relative">
                              <Input type="number" step="0.01" min="0" value={v.bonus_rid ?? 0} onChange={(e) => updateField(c.id, 'bonus_rid', e.target.value)} className="h-9 pr-8" data-testid={`bonus-rid-${c.id}`} />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">€</span>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs text-slate-500">Bonus Bolletta Web</label>
                            <div className="relative">
                              <Input type="number" step="0.01" min="0" value={v.bonus_bolletta_web ?? 0} onChange={(e) => updateField(c.id, 'bonus_bolletta_web', e.target.value)} className="h-9 pr-8" data-testid={`bonus-web-${c.id}`} />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">€</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Cascata percentuali */}
                      <div>
                        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">3. Cascata Provvigioni</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-xs text-slate-500">Agenzia <span className="text-slate-400">(1° livello sotto Master)</span></label>
                            <div className="relative">
                              <Input type="number" step="1" min="0" max="100" value={v.cascata_agenzia_pct ?? 80} onChange={(e) => updateField(c.id, 'cascata_agenzia_pct', e.target.value)} className="h-9 pr-8" />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs text-slate-500">Sub-Agenzia <span className="text-slate-400">(2°+ livello)</span></label>
                            <div className="relative">
                              <Input type="number" step="1" min="0" max="100" value={v.cascata_sub_agenzia_pct ?? 65} onChange={(e) => updateField(c.id, 'cascata_sub_agenzia_pct', e.target.value)} className="h-9 pr-8" />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Portali */}
                      <div>
                        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">4. Portali di Caricamento</p>
                        <div className="flex flex-wrap gap-1.5">
                          {PORTALI.map(p => {
                            const active = (v.portali_caricamento || []).includes(p);
                            return (
                              <button key={p} type="button" onClick={() => togglePortale(c.id, p)} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${active ? 'bg-sky-600 text-white' : 'bg-white border border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                                {p}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Octopus / PUN settings */}
                      <div>
                        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">5. Impostazioni Aggiuntive</p>
                        <div className="space-y-3">
                          <div className="flex items-start gap-2 p-2 rounded bg-amber-50/60 border border-amber-100">
                            <Info className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-800">
                              Le offerte <strong>indicizzate al PUN</strong> vengono riconosciute automaticamente dal sistema in base al loro <code className="bg-amber-100 px-1 rounded">tipo_prezzo</code>. I prezzi vengono ricalcolati a runtime usando l&apos;ultimo PUN disponibile, senza bisogno di configurazione manuale.
                            </p>
                          </div>
                          {(c.compagnia || '').toLowerCase().includes('octopus') && (
                            <div className="space-y-1">
                              <label className="text-xs text-slate-500 flex items-center gap-1"><LinkIcon className="w-3 h-3" />Link Referral Octopus</label>
                              <Input value={v.link_referral || ''} onChange={(e) => updateField(c.id, 'link_referral', e.target.value, false)} placeholder="https://share.octopusenergy.it/ref/..." className="h-9 text-sm" data-testid={`link-ref-${c.id}`} />
                              <p className="text-[10px] text-slate-400">Il link verra mostrato ai sub-agenti nel form di creazione contratto Octopus.</p>
                            </div>
                          )}
                          <div className="space-y-1">
                            <label className="text-xs text-slate-500">Note</label>
                            <Input value={v.note || ''} onChange={(e) => updateField(c.id, 'note', e.target.value, false)} placeholder="Note interne" className="h-9 text-sm" />
                          </div>
                        </div>
                      </div>

                      {/* Live preview */}
                      <div className="rounded-lg border-2 border-dashed border-sky-200 bg-sky-50/50 p-4">
                        <p className="text-xs font-semibold text-sky-700 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Info className="w-3 h-3" />Anteprima Provvigioni Calcolate</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                          {['luce', 'gas', 'dual'].map(tipo => {
                            const prev = computePreview(v, tipo);
                            if (!prev || prev.master === 0) return null;
                            return (
                              <div key={tipo} className="bg-white rounded p-3 border border-sky-100">
                                <p className="text-xs font-semibold capitalize mb-2 flex items-center gap-1">
                                  {tipo === 'luce' && <Zap className="w-3 h-3 text-amber-500" />}
                                  {tipo === 'gas' && <Flame className="w-3 h-3 text-sky-500" />}
                                  {tipo} <span className="text-slate-400 font-normal">(RID + Web)</span>
                                </p>
                                <div className="space-y-1 text-xs">
                                  <div className="flex justify-between"><span className="text-purple-700 font-medium">Master:</span><strong className="tabular-nums">{prev.master.toFixed(2)}€</strong></div>
                                  <div className="flex justify-between"><span className="text-sky-700 font-medium">Agenzia:</span><strong className="tabular-nums">{prev._agenzia.toFixed(2)}€</strong></div>
                                  <div className="flex justify-between"><span className="text-emerald-700 font-medium">Sub:</span><strong className="tabular-nums">{prev._sub.toFixed(2)}€</strong></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="flex justify-end pt-2">
                        <Button onClick={() => handleSave(c.id)} disabled={saving === c.id} data-testid={`save-comm-${c.id}`}>
                          {saving === c.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                          Salva {c.compagnia}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Commissioni;
